import { useMemo } from 'react';
import { BN, PublicKey, ZERO } from '@drift-labs/sdk';
import { Serializer, UISerializableOrderActionRecord } from '@drift/common';
import { useCurrentAccountOrders } from 'src/hooks/query/accountTradingData/useOrders';
import { useCurrentAccountPositions } from 'src/hooks/query/accountTradingData/usePositions';
import { useCurrentAccountMarginInfo } from 'src/hooks/query/accountTradingData/useMarginInfo';
import useDriftAccountStore, {
	AccountSpotBalance,
} from 'src/stores/useDriftAccountsStore';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import useDriftClient from 'src/hooks/useDriftClient';
import { useShortTermHistoryQuery } from 'src/hooks/historyData/useShortTermHistoryQuery';
import { getTradeInfoFromOrderActionRecord } from 'src/utils/tradeRecord';
import {
	buildElfaUserData,
	PositionMarginInfo,
} from '../utils/buildElfaUserData';

const EMPTY_SPOT_BALANCES: AccountSpotBalance[] = [];
const MAX_TRADES = 20;

export function useElfaUserData(
	authority: PublicKey | undefined
): Record<string, unknown> | undefined {
	const orders = useCurrentAccountOrders();
	const positions = useCurrentAccountPositions();
	const marginInfo = useCurrentAccountMarginInfo();
	const spotBalances =
		useDriftAccountStore((s) => s.getCurrentUserAccount()?.spotBalances) ??
		EMPTY_SPOT_BALANCES;
	const borrowLendData = useDriftStore((s) => s.borrowLendData);
	const predictedFundings = useDriftStore((s) => s.predictedFundings);
	const driftClient = useDriftClient();

	const { allRecords: tradeRecords } =
		useShortTermHistoryQuery<UISerializableOrderActionRecord>(
			'trades',
			Serializer.Deserialize.DataApiUIOrderActionRecord,
			!!authority
		);

	const positionMarginInfoMap = useMemo(() => {
		const map = new Map<number, PositionMarginInfo>();
		if (!driftClient?.hasUser()) return map;

		const user = driftClient.getUser();
		for (const pos of positions) {
			const sdkPerp = user.getPerpPosition(pos.marketIndex);
			const isIsolated = sdkPerp ? user.isPerpPositionIsolated(sdkPerp) : false;

			let isolatedMargin: BN | null = null;
			if (isIsolated) {
				const deposit =
					driftClient.getIsolatedPerpPositionTokenAmount(pos.marketIndex) ??
					ZERO;
				const unsettledPnl = pos.unsettledPnl ?? ZERO;
				isolatedMargin = deposit.add(unsettledPnl);
			}

			map.set(pos.marketIndex, { isIsolated, isolatedMargin });
		}
		return map;
	}, [driftClient, positions]);

	const recentTrades = useMemo(() => {
		if (!authority || tradeRecords.length === 0) return [];

		return tradeRecords
			.slice(0, MAX_TRADES)
			.map((record) => getTradeInfoFromOrderActionRecord(authority, record));
	}, [authority, tradeRecords]);

	return useMemo(() => {
		if (!authority) return undefined;

		return buildElfaUserData({
			authority: authority.toBase58(),
			orders,
			positions,
			spotBalances,
			marginInfo,
			borrowLendData,
			predictedFundings,
			positionMarginInfoMap,
			recentTrades,
		});
	}, [
		authority,
		orders,
		positions,
		spotBalances,
		marginInfo,
		borrowLendData,
		predictedFundings,
		positionMarginInfoMap,
		recentTrades,
	]);
}
